import React, {
  useEffect,
  useRef,
  useState,
  DependencyList,
  useContext,
} from "react";
import PropTypes from "prop-types";
import Overlay from "../../Overlay/index.jsx";
import Button from "../../common/Button/index.jsx";
import { langComponent, PAGE_STATE } from "../../../constants/index.jsx";
import useBlogs from "../../../hooks/useBlogs.js";
import { ReactComponent as ArrowLeftSvg } from "../../../assets/icons/arrow-left-filled.svg";
import { Form, Formik } from "formik";
import TextBox from "../../common/TextBox/index.jsx";
import DatePicker from "../../common/DatePicker/index.jsx";
import { ReactComponent as CloseSvg } from "../../../assets/admin/close.svg";
import { ReactComponent as GearSvg } from "../../../assets/icons/gear.svg";
import { ReactComponent as ClosePrimarySvg } from "../../../assets/icons/close-primary.svg";
import {
  ClassicEditor,
  Context,
  Code,
  Bold,
  Essentials,
  Italic,
  Paragraph,
  Heading,
  BlockQuote,
  Font,
  Link,
  List,
  Underline,
  Strikethrough,
  RemoveFormat,
  Alignment,
  Indent,
  IndentBlock,
  AutoImage,
  Image,
  Base64UploadAdapter,
  ImageInsert,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageCaption,
  PictureEditing,
  MediaEmbed,
  FullPage,
  Autoformat,
  AutoLink,
  SourceEditing,
  ContextWatchdog,
  GeneralHtmlSupport,
} from "ckeditor5";
import { CKEditor, CKEditorContext } from "@ckeditor/ckeditor5-react";

import "ckeditor5/ckeditor5.css";
import "rc-slider/assets/index.css";
import { ObjectId } from "bson";
import "react-image-crop/src/ReactCrop.scss";
import { canvasPreview } from "./components/CanvasPreview.jsx";
import TextArea from "../../common/TextArea/index.jsx";
import AppContext from "../../../AppContext.jsx";
import useScreenSize from "../../../hooks/useScreenSize.js";
import { TagsInput } from "react-tag-input-component";
import _ from "lodash";
import ImageCropperEx from "../../common/ImageCropperEx/index.jsx";
import useTendering from "../../../hooks/useTendering.js";
import { toast } from "react-toastify";

const assetsURI = import.meta.env.ASSETS_URI;

function useDebounceEffect(fn, waitTime, deps) {
  useEffect(() => {
    const t = setTimeout(() => {
      fn.apply(undefined, deps);
    }, waitTime);

    return () => {
      clearTimeout(t);
    };
  }, deps);
}

const ViewBlog = ({ editing: _editing, onClose, onSuccess }) => {
  const { getSignedUrl } = useTendering();
  const [editing, setEditing] = useState(_editing);
  const [pageState, setPageState] = useState(
    editing ? PAGE_STATE.LOADING : PAGE_STATE.IDLE
  );
  const [savingState, setSavingState] = useState(PAGE_STATE.IDLE);
  const [initialValues, setInitialValues] = useState({
    name: "",
    content: "",
    category: "",
    desc: "",
    coverImage: "",
    effectiveDate: "",
    status: 1,
  });
  const [tabs, setTabs] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState([undefined]);
  const [selectedTab, setSelectedTab] = useState("Setting");
  const [cover, setCover] = useState(undefined);
  const [settingTabFile, setSettingTabFile] = useState(undefined);
  const [settingTabCrop, setSettingTabCrop] = useState(undefined);
  const [imageTabFile, setImageTabFile] = useState(undefined);
  const [imageTabCrop, setImageTabCrop] = useState(undefined);
  const [crop, setCrop] = useState(undefined);
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [completedCrop, setCompletedCrop] = useState(undefined);
  const [isMobile, setIsMobile] = useState(false);
  const [base64Img, setBase64Img] = useState("");
  const [isMobileViewTab, setIsMobileViewTab] = useState(false);
  const [tags, setTags] = useState([]);
  const [isChangeImage, setIsChangeImage] = useState(false);
  const [isUseImage, setIsUseImage] = useState(false);

  const { screenSize } = useScreenSize();
  const formikRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const imgRef = useRef(null);
  const quillRef = useRef("");
  const editorRef = useRef("");

  const tabEnum = {
    IMAGE: "Image",
    SETTING: "Setting",
  };
  const { getLang } = useContext(AppContext);
  const TranslationCommon = getLang(langComponent.common);

  let Editor = {};
  Editor.modules = {
    toolbar: [
      [{ header: "1" }, { header: "2" }, { font: [] }],
      [{ size: [] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      ["link", "image", "video"],
      ["clean"],
    ],
    clipboard: {
      // toggle to add extra line breaks when pasting HTML:
      matchVisual: false,
    },
  };
  Editor.formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
    "video",
  ];

  useEffect(() => {
    const _tabs = ["Image", "Setting"];
    const isMobileView = !["lg", "xl"].includes(screenSize);
    setTabs(_tabs);
    setIsMobile(isMobileView);
    setIsMobileViewTab(false);
    // if (isMobileView) {
    //   setIsMobileViewTab(true);
    // }
    setSelectedTab(_tabs[1]);
  }, []);

  const { getBlog, createBlog, patchBlog, uploadBlogCover, deleteBlog } =
    useBlogs();

  useEffect(() => {
    if (!editing) return;
    fetchData();
    return () => {};
  }, []);

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        // We use canvasPreview as it's much faster than imgPreview.
        await canvasPreview(
          imgRef.current,
          previewCanvasRef.current,
          completedCrop,
          scale,
          rotate
        );
      }
    },
    100,
    [completedCrop, scale, rotate]
  );

  const fetchData = async () => {
    const { name, content, tags, cover, effectiveDate, desc, status, credit } =
      await getBlog(editing);

    setCover({
      dataUrl: `${assetsURI}/${cover}`,
    });

    setInitialValues({
      name,
      content,
      desc,
      cover,
      effectiveDate,
      status,
      credit,
    });

    setTags(tags);
    setPageState(PAGE_STATE.IDLE);
  };

  const handleRemoveCropPicture = () => {
    setCover(undefined);

    if (selectedTab === tabEnum.IMAGE) {
      setImageTabFile(undefined);
      setImageTabCrop(undefined);
    } else {
      setSettingTabFile(undefined);
      setSettingTabCrop(undefined);
    }

    setIsChangeImage(true);
    setScale(1);
    setRotate(0);
  };

  const handleSettingClick = () => {
    if (settingTabFile && settingTabCrop) {
      const url = URL.createObjectURL(settingTabFile);
      // setNewImageFile(url);
      setCrop(settingTabCrop);
    } else {
      // setNewImageFile(undefined);
    }

    setIsChangeImage(false);
    setIsUseImage(false);

    if (isMobile) {
      setIsMobileViewTab(true);
      if (base64Img) {
        const quill = quillRef.current.editor;
        if (quill) {
          const quillRange = quill.getSelection();
          let position = quillRange ? quillRange.index : -1;
          quill.insertEmbed(position, "image", base64Img);
        }
      }
    } else {
      setIsMobileViewTab(false);
    }
  };

  const _uploadFile = async (key, file) => {
    const signedUrl = await getSignedUrl(key, file.type);

    const uploadResponse = await fetch(signedUrl, {
      method: "PUT",
      headers: {
        "Content-Type": file.type,
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: 0,
      },
      body: file,
    });

    if (uploadResponse.status !== 200) {
      return false;
    }

    return true;
  };

  const handleSubmit = async (
    { name, desc, credit, effectiveDate },
    options
  ) => {
    setSavingState(PAGE_STATE.LOADING);
    // * Edit Blog
    if (editing) {
      try {
        if (!cover.blob) {
          await patchBlog(editing, {
            name,
            content: initialValues.content,
            category: selectedCategory,
            status: initialValues.status,
            effectiveDate: effectiveDate,
            desc,
            tags,
            credit,
          });

          setSavingState(PAGE_STATE.IDLE);
          onSuccess();
          return;
        }

        // * Upload signature and stamp
        const coverId = new ObjectId();
        const coverKey = `blogs/${editing}/covers/${coverId}`;
        const coverFile = new File([cover.blob], "filename", {
          type: "image/jpeg",
        });

        if (!(await _uploadFile(coverKey, coverFile))) {
          toast.error("Unable to upload cover image");
          return;
        }

        await patchBlog(editing, {
          name,
          content: initialValues.content,
          category: selectedCategory,
          status: initialValues.status,
          effectiveDate: effectiveDate,
          desc,
          tags,
          credit,
          cover: coverKey,
        });

        setSavingState(PAGE_STATE.IDLE);
        onSuccess();
        return;
      } catch (error) {
        console.error(error.message);
        setSavingState(PAGE_STATE.ERROR);
      }
    }

    // * Create new blog
    if (!editing) {
      try {
        const blog = await createBlog({
          name,
          content: initialValues.content,
          category: selectedCategory,
          status: initialValues.status,
          effectiveDate: effectiveDate,
          tags,
          desc,
          credit,
        });

        if (!cover) {
          setSavingState(PAGE_STATE.IDLE);
          onSuccess();
          return;
        }

        // * Upload signature and stamp
        const coverId = new ObjectId();
        const coverKey = `blogs/${blog._id}/covers/${coverId}`;
        const coverFile = cover.blob
          ? new File([cover.blob], cover.file.name, {
              type: cover.file.type,
            })
          : cover.file;

        if (!(await _uploadFile(coverKey, coverFile))) {
          toast.error("Unable to upload cover image");
          return;
        }

        await patchBlog(blog._id, {
          cover: coverKey,
        });

        setSavingState(PAGE_STATE.IDLE);
        onSuccess();
        return;
      } catch (error) {
        console.error(error.message);
        setSavingState(PAGE_STATE.ERROR);
      }
    }
  };

  const handleMobileViewTab = () => {
    setIsMobileViewTab(false);
  };

  const handleChangeContent = () => {
    const editor = editorRef.current.editor;
    const currentData = editor.getData();
    setInitialValues({ ...initialValues, content: currentData });
  };

  const handleTags = (tag) => {
    if (tag.length > 10) {
      tag.pop();
      return;
    } else {
      setTags(tag);
    }
  };

  const handleCropped = async (file) => {
    setCover(file);
  };

  const _getSettings = () => {
    // if (cover && typeof cover == "string") {
    //   setNewImageFile(cover);
    // }

    return (
      <div
        className={`admin-bp-add-form__settings ${
          isMobileViewTab ? "admin-bp-add-form__settings--mobile" : ""
        }`}
      >
        {(!isMobile || isMobileViewTab) && (
          <div className="admin-bp-add-form__settings__content">
            <div className="admin-bp-add-form__settings__content__cover-title">
              Cover Image
            </div>
            <div className="admin-bp-add-form__settings__content__cropper">
              <ImageCropperEx
                name={"cover"}
                multiple={false}
                onChange={(images) => {
                  if (!images || images.length === 0) {
                    handleRemoveCropPicture();
                    return;
                  }

                  handleCropped(images[0]);
                }}
                defaultImages={cover ? [cover] : undefined}
              />
            </div>
            <TextBox label="Name" name="name" />
            <TextBox label="End Credit" name="credit" />
            <TextArea
              label="Description"
              placeholder={"Blog Description"}
              name={"desc"}
            />
            <DatePicker
              id={editing}
              name="effectiveDate"
              label="Publish Date"
              dateFormat="dd/MM/yyyy"
              defaultValue={
                initialValues.effectiveDate
                  ? initialValues.effectiveDate
                  : new Date()
              }
            />
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: "#333",
                  margin: "0 8px",
                }}
              >
                Tags (Max limit of 10)
              </label>
              <TagsInput
                id={"blog-tag-input"}
                value={tags}
                onChange={(tag) => {
                  handleTags(tag);
                }}
                beforeAddValidate={(tag, existingTags) => {
                  if (existingTags.length + 1 > 10) {
                    return false;
                  } else {
                    return true;
                  }
                }}
                name="Tag"
                placeHolder="Tag"
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    const isMobileView = !["lg", "xl"].includes(screenSize);

    setIsMobile(isMobileView);

    if (!isMobile) {
      setIsMobileViewTab(false);
    }
  }, [screenSize]);

  if (pageState === PAGE_STATE.LOADING) {
    return <Overlay />;
  }

  return (
    <Overlay
      onClose={() => {
        onClose();
      }}
      style={{
        alignItems: "flex-start",
      }}
    >
      <div className="admin-bp-add">
        <div className="project-view-close" onClick={onClose}>
          <CloseSvg />
          {TranslationCommon("Close")}
        </div>
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          {(props) => {
            return (
              <Form
                className={`admin-bp-add-form ${
                  isMobile ? "admin-bp-add-form--mobile" : ""
                }`}
              >
                <div className="admin-bp-add-form__ctas">
                  {!isMobileViewTab && (
                    <div className={"admin-bp-add-form__ctas-action"}>
                      <Button label="Publish" type="submit" />
                      {isMobile && (
                        <ClosePrimarySvg
                          width={24}
                          height={24}
                          onClick={() => onClose()}
                        />
                      )}
                    </div>
                  )}
                  {isMobile && isMobileViewTab && (
                    <div
                      className="admin-bp-add-form__ctas-action"
                      style={{ flexGrow: 1 }}
                    >
                      <ArrowLeftSvg
                        width={18}
                        height={18}
                        onClick={handleMobileViewTab}
                      />
                    </div>
                  )}
                  {isMobile && !isMobileViewTab && (
                    <div className="admin-bp-add-form__ctas-action">
                      <GearSvg
                        width={24}
                        height={24}
                        onClick={() => handleSettingClick()}
                      />
                    </div>
                  )}
                </div>
                {_getSettings()}
                <div
                  className={
                    !isMobile || isMobileViewTab
                      ? "admin-bp-add-form__editor"
                      : "admin-bp-add-form__editor admin-bp-add-form__editor--mobile"
                  }
                  style={{ display: isMobileViewTab ? "none" : "flex" }}
                >
                  <CKEditorContext
                    context={Context}
                    contextWatchdog={ContextWatchdog}
                  >
                    <CKEditor
                      editor={ClassicEditor}
                      config={{
                        placeholder: "Type or paste your content here!",
                        plugins: [
                          Essentials,
                          Font,
                          Heading,
                          Code,
                          Bold,
                          Italic,
                          Paragraph,
                          Underline,
                          Strikethrough,
                          BlockQuote,
                          List,
                          Indent,
                          IndentBlock,
                          Link,
                          AutoImage,
                          Image,
                          ImageInsert,
                          ImageResize,
                          ImageStyle,
                          ImageToolbar,
                          ImageCaption,
                          PictureEditing,
                          MediaEmbed,
                          FullPage,
                          Alignment,
                          RemoveFormat,
                          Autoformat,
                          AutoLink,
                          Base64UploadAdapter,
                          SourceEditing,
                          GeneralHtmlSupport,
                        ],
                        toolbar: [
                          "undo",
                          "redo",
                          "|",
                          "heading",
                          "fontSize",
                          "fontColor",
                          "fontBackgroundColor",
                          "bold",
                          "italic",
                          "alignment",
                          "underline",
                          "strikethrough",
                          "removeFormat",
                          "blockQuote",
                          "|",
                          "bulletedList",
                          "numberedList",
                          "outdent",
                          "indent",
                          "|",
                          "link",
                          "insertImage",
                          "mediaEmbed",
                          "sourceEditing",
                        ],
                        fontSize: {
                          options: [
                            9,
                            11,
                            13,
                            "default",
                            17,
                            19,
                            21,
                            24,
                            32,
                            38,
                            40,
                          ],
                        },
                        image: {
                          resizeOptions: [
                            {
                              name: "resizeImage:original",
                              label: "Default image width",
                              value: null,
                            },
                            {
                              name: "resizeImage:50",
                              label: "50% page width",
                              value: "50",
                            },
                            {
                              name: "resizeImage:75",
                              label: "75% page width",
                              value: "75",
                            },
                          ],
                          toolbar: [
                            "imageTextAlternative",
                            "toggleImageCaption",
                            "|",
                            "imageStyle:inline",
                            "imageStyle:wrapText",
                            "imageStyle:breakText",
                            "|",
                            "resizeImage",
                            "|",
                            "editImage",
                          ],
                        },
                        htmlSupport: {
                          allow: [
                            // Enables all HTML features.
                            {
                              name: /.*/,
                              attributes: true,
                              classes: true,
                              styles: true,
                            },
                          ],
                          disallow: [
                            {
                              attributes: [
                                { key: /^on(.*)/i, value: true },
                                {
                                  key: /.*/,
                                  value:
                                    /(\b)(on\S+)(\s*)=|javascript:|(<\s*)(\/*)script/i,
                                },
                                {
                                  key: /.*/,
                                  value: /data:(?!image\/(png|jpeg|gif|webp))/i,
                                },
                              ],
                            },
                            { name: "script" },
                          ],
                        },
                      }}
                      data={initialValues.content}
                      onChange={handleChangeContent}
                      onReady={(editor) => {
                        editorRef.current = { editor };
                      }}
                    />
                  </CKEditorContext>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </Overlay>
  );
};

ViewBlog.propTypes = {
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  editing: PropTypes.string,
};

ViewBlog.defaultProps = {
  onClose: () => {},
  onSuccess: () => {},
  editing: undefined,
};

export default ViewBlog;
